import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { GridContainer, GridItem } from './ui-extension';
import { LabeledInput } from './labeled-input';
import {
  getCompanyContactOptions,
  getNoteClassOptions,
} from '../../api/options';
import { useNotificationContext } from './notification';
import { NotesAPI, getNoteAreaOptions } from '../../api/notes';
import Modal from './modal';
import { customerVisitNoteTypes } from '../customer-page/shared';

function AddOrEditNotesDialog({
  open,
  setOpen,
  note,
  setNote,
  data,
  showTypeOptions,
  onNotesModified,
  filterClassOptions,
  supplierOptions,
  additionalAddHeaderDetails = null,
  showHideNoteCheckbox = false,
}) {
  const [noteClassOptions, setNoteClassOptions] = useState([]);
  const [noteAreaOptions, setNoteAreaOptions] = useState([]);
  const { showMessage, useExceptionHandler } = useNotificationContext();
  const [employees, setEmployees] = useState([]);

  const { noteType } = data;

  useEffect(() => {
    getNoteAreas();
  }, [data.noteType, data.defaultArea]);

  useEffect(() => {
    getCompanyContacts();

    return () => {
      setEmployees([]);
    };
  }, []);

  const getCompanyContacts = useExceptionHandler(async () => {
    const options = (await getCompanyContactOptions())
      ?.filter((c) => c.user_id)
      .map((c) => {
        c.value = c.user_id;
        return c;
      });
    setEmployees(options);
  });

  const getNoteAreas = useExceptionHandler(async () => {
    const { noteType, defaultArea } = data;

    const noteClassOptionsData = await getNoteClassOptions();

    if (filterClassOptions) {
      setNoteClassOptions(filterClassOptions(noteClassOptionsData));
    } else {
      setNoteClassOptions(noteClassOptionsData);
    }

    const noteAreaOptionsData = await getNoteAreaOptions({
      noteType,
    });

    let defaultOption = noteAreaOptionsData.find(
      (a) => a.value === defaultArea
    );

    if (defaultOption) {
      defaultOption.checked = true;
      defaultOption.disabled = true;
    }

    setNoteAreaOptions(noteAreaOptionsData);
  });

  const editNote = useExceptionHandler(async (note) => {
    await NotesAPI.editNote({ noteType, note });
    onNotesModified();
    setOpen(false);
  });

  const addNote = useExceptionHandler(async (note) => {
    const { data: addedNoteData } = await NotesAPI.addNote({
      noteType,
      note,
    });
    setNote(addedNoteData);
    onNotesModified();
    setOpen(false);
  });

  const handleAreaChange = (event) => {
    setNote({
      ...note,
      areas: [...new Set(event.target.value)],
    });
  };

  return (
    <Modal
      title='Add/Edit Note'
      open={open}
      setOpen={setOpen}
      dialogContentSx={{ overflowX: 'hidden' }}
      backdropWarningMessage='Use the X to close. Any unsaved changes may be lost.'
      actions={
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            if (!note.areas?.length || !note.topic) {
              showMessage({
                message: `Please fill in all required fields.`,
                severity: 'error',
              });
              return;
            }
            if (supplierOptions && !note.supplier_id) {
              showMessage({
                message: `Please select a supplier.`,
                severity: 'error',
              });
              return;
            }
            if (note.isNew) {
              addNote(note);
            } else {
              editNote(note);
            }
          }}
        >
          Save
        </Button>
      }
    >
      <GridContainer columns={2} alignItems='center' rowSpacing={2}>
        {additionalAddHeaderDetails ? (
          <GridItem xs={12}>{additionalAddHeaderDetails()}</GridItem>
        ) : null}
        <GridItem xs={2}>
          <FormControl sx={{ minWidth: 550 }} required>
            <InputLabel id='demo-multiple-checkbox-label'>
              Select Areas
            </InputLabel>
            <Select
              labelId='demo-multiple-checkbox-label'
              id='demo-multiple-checkbox'
              multiple
              value={note.areas || []}
              onChange={handleAreaChange}
              input={<OutlinedInput label='Tag' />}
              error={!note.areas?.length}
              renderValue={(selected) => {
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((area) => (
                      <Chip key={area} label={area} size='small' />
                    ))}
                  </Box>
                );
              }}
            >
              {noteAreaOptions?.map((area) => (
                <MenuItem
                  key={area.value}
                  value={area.value}
                  disabled={area.disabled || false}
                >
                  <Checkbox
                    checked={
                      area.checked || note.areas?.indexOf(area.value) > -1
                    }
                  />
                  <ListItemText primary={area.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </GridItem>
        {showTypeOptions ? (
          <GridItem xs={2}>
            <LabeledInput
              type='select-search'
              variant='outlined'
              label='Type'
              value={note.notepad_class_id}
              options={noteClassOptions}
              groupBy={(o) => o.label?.split(' ')[0] || ''}
              onChange={(e) => {
                const noteClassDescription = noteClassOptions.find(
                  (c) => c.value === e.target.value
                )?.label;

                setNote((prev) => ({
                  ...prev,
                  notepad_class_id: e.target.value,
                  notepad_class_desc: noteClassDescription,
                  topic: noteClassDescription?.substring(0, 30),
                }));
              }}
              error={!note.notepad_class_id}
            />
          </GridItem>
        ) : null}

        {supplierOptions ? (
          <GridItem xs={2}>
            <LabeledInput
              type='select-search'
              variant='outlined'
              label='Supplier'
              value={note.supplier_id}
              options={supplierOptions}
              onChange={(e) =>
                setNote({
                  ...note,
                  supplier_id: e.target.value,
                  noteEntityKey: e.target.value,
                })
              }
              error={!note.supplier_id}
            />
          </GridItem>
        ) : null}

        <GridItem xs={2}>
          <LabeledInput
            label='Topic'
            variant='outlined'
            value={note.topic}
            onChange={(e) => setNote({ ...note, topic: e.target.value })}
            additionalProps={{
              inputProps: { maxLength: 30 },
              required: true,
            }}
            error={!note.topic}
          />
        </GridItem>
        {customerVisitNoteTypes.includes(note.notepad_class_id) ? (
          <>
            <GridItem xs={1}>
              <LabeledInput
                type='select-search'
                label='Visited By'
                value={note.visited_by}
                options={employees}
                onChange={(e) =>
                  setNote({ ...note, visited_by: e.target.value })
                }
                error={!note.visited_by}
              />
            </GridItem>
            <GridItem xs={1}>
              <LabeledInput
                type={'date'}
                label='Date Visited'
                value={note.activation_date}
                onChange={(date) => setNote({ ...note, activation_date: date })}
                error={!note.visited_by}
              />
            </GridItem>
          </>
        ) : (
          <GridItem>
            <LabeledInput
              type={'date'}
              variant='outlined'
              label='Expiration Date'
              value={note.expiration_date}
              onChange={(date) => setNote({ ...note, expiration_date: date })}
            />
          </GridItem>
        )}
        <GridItem xs={2}>
          <LabeledInput
            type={'checkbox'}
            label={
              data.noteType === 'VENDOR' || data.noteType === 'SUPPLIER'
                ? 'Mandatory (prioritize at top of list)'
                : 'Mandatory'
            }
            value={note.mandatory}
            onChange={(e) =>
              setNote({ ...note, mandatory: e.target.checked ? 'Y' : 'N' })
            }
          />
        </GridItem>
        {showHideNoteCheckbox && (
          <GridItem xs={2}>
            <LabeledInput
              type={'checkbox'}
              label='Hide Note From Home Screen'
              value={note.hide_note}
              onChange={(e) =>
                setNote({ ...note, hide_note: e.target.checked ? 'Y' : 'N' })
              }
            />
          </GridItem>
        )}
        <GridItem xs={2}>
          <LabeledInput
            label='Note'
            value={note.note}
            additionalProps={{
              multiline: true,
              rows: 10,
              variant: 'outlined',
            }}
            onChange={(e) => setNote({ ...note, note: e.target.value })}
          />
        </GridItem>
      </GridContainer>
    </Modal>
  );
}

export default AddOrEditNotesDialog;
